import React, { useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { invoiceCalculations } from 'modules/invoice';
import { FieldPath, useFormContext } from 'react-hook-form';
import { GetDealInfoFromExternalDmsResponse, PaymentType } from '@trnsact/trnsact-shared-types';
import { CurrencyInputField, SelectField } from 'components/form';
import { FormSectionHeaderProps, FormSectionHeader } from 'components/shared/FormSectionHeader';
import { SaleType } from '../../types';
import { saleTypeOptions } from './constants';
import { DeskingSaleDetailsFormPart, SaleDetailsValues } from './types';

interface Props<Value extends string> extends FormSectionHeaderProps<Value> {
  isSupportSaleDetailsUpdating: boolean;
  onSaleDetailsValuesChange?: <Key extends keyof SaleDetailsValues>(key: Key, value: SaleDetailsValues[Key]) => void;
  externalDealInfo?: GetDealInfoFromExternalDmsResponse | null;
}

export const DeskingSaleDetailsFormSection = <Value extends string>({
  onSaleDetailsValuesChange,
  isSupportSaleDetailsUpdating = false,
  externalDealInfo = null,
  ...headerProps
}: Props<Value>) => {
  const { control, setValue, getValues } = useFormContext<DeskingSaleDetailsFormPart>();

  const updateInvoiceTotal = () => {
    const {
      saleDetails: { downPayment, equipmentSubtotal, salesTax, netRate, fees },
    } = getValues();

    const commonParams = { equipmentCost: equipmentSubtotal, salesTax, totalFees: fees, netRate };

    const invoiceTotal = invoiceCalculations.invoiceTotal(commonParams);
    const downPaymentPercent = invoiceCalculations.calcDownPaymentPercent(invoiceTotal, downPayment.flat);

    setValue('saleDetails.invoiceTotal', invoiceTotal);
    setValue('saleDetails.downPayment.percent', downPaymentPercent);
    setValue(
      'saleDetails.financeAmount',
      invoiceCalculations.netFinanceAmount({ ...commonParams, downPayment: downPayment.flat })
    );
  };

  const calcDownPaymentPercent = () => {
    const {
      saleDetails: { invoiceTotal, downPayment },
    } = getValues();

    const downPaymentPercent = invoiceCalculations.calcDownPaymentPercent(invoiceTotal, downPayment.flat);
    setValue('saleDetails.downPayment.percent', downPaymentPercent);
  };

  const handleUpdateValueWithDependence = (
    field: FieldPath<DeskingSaleDetailsFormPart>,
    nextValue: number,
    deps: VoidFunction[]
  ) => {
    setValue(field, nextValue);
    deps.forEach(fn => fn());
  };

  useEffect(() => {
    if (!externalDealInfo) {
      return;
    }

    let saleType: SaleType = SaleType.Financing;

    if (externalDealInfo.paymentType === PaymentType.Cash) {
      saleType = SaleType.Cash;
    }

    const equipmentSubtotal = externalDealInfo.equipmentSubtotal;
    const salesTax = externalDealInfo.salesTaxAmount;
    const fees = externalDealInfo.totalFees;
    const netTrade = externalDealInfo.netTrade;
    const downPaymentAmount = externalDealInfo.downPaymentAmount;

    if (saleType != null) {
      setValue('saleDetails.saleType', saleType);
      onSaleDetailsValuesChange?.('saleType', saleType);
    }

    if (equipmentSubtotal != null) {
      setValue('saleDetails.equipmentSubtotal', Number(equipmentSubtotal));
    }

    if (salesTax != null) {
      setValue('saleDetails.salesTax', Number(salesTax));
      onSaleDetailsValuesChange?.('salesTax', Number(salesTax));
    }

    if (fees != null) {
      setValue('saleDetails.fees', Number(fees));
      onSaleDetailsValuesChange?.('fees', Number(fees));
    }

    if (netTrade != null) {
      setValue('saleDetails.netRate', Number(netTrade));
      onSaleDetailsValuesChange?.('netRate', Number(netTrade));
    }

    if (downPaymentAmount != null) {
      setValue('saleDetails.downPayment.flat', Number(downPaymentAmount));
    }

    // IMPORTANT: Must be called at the end to ensure all dependent values are set
    updateInvoiceTotal();
  }, [externalDealInfo, setValue]);

  return (
    <Stack gap="0.5rem">
      <FormSectionHeader name="Sale Details" {...headerProps} />

      <Stack gap="1rem">
        <SelectField
          control={control}
          label="Sale Type"
          options={saleTypeOptions}
          name="saleDetails.saleType"
          extraAction={nextValue => {
            onSaleDetailsValuesChange?.('saleType', nextValue.toString() as SaleType.Cash);
          }}
        />

        <CurrencyInputField
          control={control}
          label="Equipment Subtotal"
          name="saleDetails.equipmentSubtotal"
          textFieldProps={{ disabled: !isSupportSaleDetailsUpdating }}
          customOnChange={(value, name) => {
            handleUpdateValueWithDependence(name, value, [updateInvoiceTotal]);
          }}
        />

        <Box className="row">
          <CurrencyInputField
            control={control}
            label="Sales Tax"
            name="saleDetails.salesTax"
            textFieldProps={{ disabled: !isSupportSaleDetailsUpdating }}
            customOnChange={(nextValue, name) => {
              onSaleDetailsValuesChange?.('salesTax', nextValue);
              handleUpdateValueWithDependence(name, nextValue, [updateInvoiceTotal]);
            }}
          />

          <CurrencyInputField
            control={control}
            label="Total Fees"
            name="saleDetails.fees"
            textFieldProps={{ disabled: !isSupportSaleDetailsUpdating }}
            customOnChange={(nextValue, name) => {
              onSaleDetailsValuesChange?.('fees', nextValue);
              handleUpdateValueWithDependence(name, nextValue, [updateInvoiceTotal]);
            }}
          />

          <CurrencyInputField
            control={control}
            label="Net trade"
            name="saleDetails.netRate"
            textFieldProps={{ disabled: !isSupportSaleDetailsUpdating }}
            customOnChange={(nextValue, name) => {
              onSaleDetailsValuesChange?.('netRate', nextValue);
              handleUpdateValueWithDependence(name, nextValue, [updateInvoiceTotal]);
            }}
          />
        </Box>

        <Box className="row">
          <CurrencyInputField
            control={control}
            label="Invoice Total"
            name="saleDetails.invoiceTotal"
            textFieldProps={{ disabled: true }}
          />

          <CurrencyInputField
            control={control}
            label="Down Payment ($)"
            name="saleDetails.downPayment.flat"
            textFieldProps={{ disabled: !isSupportSaleDetailsUpdating }}
            customOnChange={(nextValue, name) => {
              handleUpdateValueWithDependence(name, nextValue, [calcDownPaymentPercent, updateInvoiceTotal]);
            }}
          />

          <CurrencyInputField
            control={control}
            label="Net finance amount"
            name="saleDetails.financeAmount"
            textFieldProps={{ disabled: true }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};
